// Day supplier for the two cascade "Phase 2" future searches.
//
// Those loops walk forward one day at a time, up to 90 days, and used to
// `await fetchEqpPlanner(dateStr)` on every single iteration -- 14 sequential
// round trips at the very least (measured 9.7s on dev, 1.9s on staging), even
// when nothing was found.
//
// This fetches the candidate days in concurrent chunks and hands them back in
// order, so the loop body, its filters and its early-exit stay exactly as they
// were. Safe because the candidate dates are `startDate + 0..maxDays`, fixed
// before the loop begins -- only *when to stop* depends on the data, and the
// early exit still decides that, so at most one extra chunk is ever fetched
// (and that chunk cost one round trip, not 14).
//
import dayjs from 'dayjs';

// Lives outside the component so the "chunked returns exactly what sequential
// returned" property can be tested with an injected fake.

/**
 * @param fetchDay  (dateString) => Promise<response>  -- injected, so tests do
 *                  not need the network. In the app this is fetchEqpPlanner.
 * @param startDate dayjs-like object supporting .add(n,'day').format(...)
 * @param maxDays   how many candidate days exist at all (the loop's own cap)
 * @param chunkSize how many days to fetch concurrently per round trip
 * @returns (dayOffset) => Promise<response|null>
 *          Resolves with the day's response, or null past `maxDays`.
 *          **Rethrows** a day's fetch error on access, so a caller's existing
 *          per-day try/catch behaves identically to the sequential version:
 *          warn, and move on without counting the day as empty.
 */
export const createDayFetcher = (fetchDay, startDate, maxDays, chunkSize = 14) => {
  const cache = new Map();
  let fetchedUpTo = 0;

  const fetchChunk = async () => {
    const from = fetchedUpTo;
    const to = Math.min(from + chunkSize, maxDays);
    const offsets = [];
    for (let offset = from; offset < to; offset += 1) offsets.push(offset);

    // Settle each day independently: one bad day must not discard the other
    // thirteen, and its error has to survive until the caller asks for it.
    const settled = await Promise.all(offsets.map(offset =>
      fetchDay(startDate.add(offset, 'day').format('YYYY-MM-DD'))
        .then(value => ({ value }))
        .catch(error => ({ error }))
    ));

    offsets.forEach((offset, i) => cache.set(offset, settled[i]));
    fetchedUpTo = to;
  };

  return async (dayOffset) => {
    if (dayOffset < 0 || dayOffset >= maxDays) return null;

    // Keep pulling chunks until this offset is covered. A `while` rather than a
    // single fetch, so an out-of-order or skipping caller still gets real data
    // instead of a silent null.
    while (!cache.has(dayOffset) && fetchedUpTo < maxDays) {
      await fetchChunk();
    }

    const entry = cache.get(dayOffset);
    if (!entry) return null;
    if (entry.error) throw entry.error;
    return entry.value;
  };
};


/* ------------------------------------------------------------------ *
 * Range-based day supplier
 *
 * The day-by-day walk above is why the cascade had two limits baked in:
 * scanning cost one HTTP request per day, so it gave up after 14 empty days
 * and never looked past 90. Measured on the dev backend:
 *
 *     14 sequential day requests   9.73s   <- the old minimum
 *     90 sequential day requests  ~53s
 *     ONE range request, 365 days  2.29s   <- this
 *
 * A full year in one request is about four times cheaper than the old
 * fourteen-day crawl, so neither limit needs to exist any more. The cascade
 * can follow the chain as far as the plan actually goes.
 *
 * This maps the range response back into the per-day shape the cascade loops
 * already consume, so their bodies, filters and payload building stay exactly
 * as they were -- only where the data comes from changes.
 * ------------------------------------------------------------------ */

/** One row of /activities-by-area -> the shape /area-planner?date= returns. */
export const toPlanActivity = (row) => ({
  id: row.plan_id,
  equipment_id: row.plan_area_id || row.equipment_id,
  equipment_name: row.plan_area_name || row.equipment_name,
  eqp_id: row.plan_area_id || row.equipment_id,
  area_id: row.plan_area_id || row.area_id,
  area_name: row.plan_area_name || row.area_name,
  activity_type: row.activity_type,
  batch_number: row.plan_product || null,
  product_name: row.plan_product_name || '',
  cleaning_type: row.cleaning_type || null,
  maintenance_type: row.maintenance_type || null,
  // Both spellings: the cascade reads `start_date || start_time`.
  start_time: row.plan_start_date,
  end_time: row.plan_end_date,
  start_date: row.plan_start_date,
  end_date: row.plan_end_date,
  option_selected: row.option_selected || '',
  remarks: row.remarks || '',
});

/** Whole days between two dates, floor'd, for bucketing a row across the days it spans. */
const dayIndex = (startDate, when) =>
  Math.floor(when.startOf('day').diff(startDate.startOf('day'), 'day', true));

/**
 * Same contract as createDayFetcher -- (dayOffset) => Promise<response|null> --
 * but backed by a single range request instead of one request per day.
 *
 * A row is served on EVERY day it overlaps, which is what the per-day endpoint
 * did; the cascade de-duplicates through processedActivityIds either way.
 *
 * @param fetchRange (fromISO, toISO) => Promise<activities-by-area response>
 */
export const createRangeDayFetcher = (fetchRange, startDate, maxDays) => {
  let byDay = null;
  let failure = null;

  const load = async () => {
    const from = startDate.format('YYYY-MM-DD');
    const to = startDate.add(Math.max(0, maxDays - 1), 'day').format('YYYY-MM-DD');
    const res = await fetchRange(from, to);

    const rows = Object.values(res?.data || {})
      .flatMap(area => [...(area.matched || []), ...(area.not_started || [])])
      // `unplanned` entries are log-book actuals with no plan row behind them;
      // plan_id is what separates a real plan row from one of those.
      .filter(row => row && row.plan_id != null && row.plan_start_date)
      .map(toPlanActivity);

    const map = new Map();
    rows.forEach((row) => {
      const s = dayjs(row.start_time);
      const e = row.end_time ? dayjs(row.end_time) : s;
      const first = Math.max(0, dayIndex(startDate, s));
      const last = Math.min(maxDays - 1, dayIndex(startDate, e));
      for (let d = first; d <= last; d += 1) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(row);
      }
    });
    byDay = map;
  };

  return async (dayOffset) => {
    if (dayOffset < 0 || dayOffset >= maxDays) return null;
    if (failure) throw failure;
    if (!byDay) {
      try {
        await load();
      } catch (err) {
        // Mirror the per-day fetcher: surface the error to the caller's own
        // try/catch rather than silently pretending the days were empty.
        failure = err;
        throw err;
      }
    }
    return { activities: byDay.get(dayOffset) || [] };
  };
};

export default createDayFetcher;
